/**
 * CNC Popup — native signup (replaces Alia + Klaviyo).
 * 3-step: poll -> email -> success (geo-aware free-shipping thank-you).
 * Posts to OUR endpoint (email-signup): captures the signup (email + country + fandom) and logs
 * every show + signup, tagged with the trigger, for conversion tracking. Suppression uses OUR
 * signal (data-subscribed = logged-in customer already on the list) + localStorage — no Klaviyo.
 *
 * Minimised tab (Tracy 2026-07-28): if they close WITHOUT signing up, we don't nag with the full
 * popup again — we leave a small corner tab so they can re-open it later. Signing up removes it.
 * RFC-EMAIL-001 C7 / RFC-DAWN-002.
 */
(function () {
  'use strict';
  var DISMISS_KEY = 'cnc_popup_dismissed_at_v1';
  /* Retiring the TAB is a stronger signal than closing the popup: closing says
     "not now", the X on the strip says "take it off my screen". Separate key so
     the two cooldowns cannot overwrite each other. */
  var TAB_OFF_KEY = 'cnc_popup_tab_off_at_v1';
  var TAB_OFF_DAYS = 90;
  var SUBMITTED_KEY = 'cnc_popup_submitted_v1';
  var ID_KEY = 'cnc_popup_id_v1';
  var DEFAULT_ENDPOINT = 'https://dkzndqkjjzhmmcebqoib.supabase.co/functions/v1/email-signup';

  function init() {
    var root = document.querySelector('[data-cnc-popup]');
    if (!root) return; // section disabled or not on page

    // STRICT — never on the Library. It is ad-free by design (Tracy 2026-07-19: Alia/EasyGift
    // "should only be on the ecommerce side"); the join is inline + last there. Belt+suspenders:
    // the dedicated library layout (body.lib-shell) OR any /library route, in case a Library page
    // is served by the DEFAULT layout (where this script loads via overlay-group).
    if (document.body.classList.contains('lib-shell') ||
        /\/(pages\/)?(the-)?library(\/|$)/i.test(location.pathname)) return;

    var tab = document.querySelector('[data-cnc-popup-tab]');
    var tabWrap = document.querySelector('[data-cnc-popup-tabwrap]');
    var tabDismiss = document.querySelector('[data-cnc-popup-tab-dismiss]');

    var triggerSeconds = parseInt(root.getAttribute('data-trigger-seconds') || '45', 10);
    /* ONE THRESHOLD, EVERY PAGE (Tracy 2026-09-22: "let's site-wide"). A
       product-page-only threshold was built and rejected as not worth the second
       code path — the live value is set in the theme editor, which is site-wide by
       design, so the code must not quietly disagree with it. */
    var triggerScroll = parseInt(root.getAttribute('data-trigger-scroll') || '50', 10);
    var suppressDays = parseInt(root.getAttribute('data-suppress-days') || '5', 10);
    var country = (root.getAttribute('data-country') || 'US').toUpperCase();
    var endpoint = root.getAttribute('data-endpoint') || DEFAULT_ENDPOINT;
    var alreadySubscribed = root.getAttribute('data-subscribed') === '1';
    /* ARRIVED FROM ONE OF OUR EMAILS (Tracy 2026-10-06: the sale sends go to ~50,000 people already on
       the list). Our links carry utm_medium=email; older Klaviyo links carry _kx. Asking a subscriber
       to sign up for free shipping is noise, and counting them would flood the 50/50 test with people
       it isn't about. Remembered in this browser, so their later visits stay quiet too. */
    try {
      if (/[?&](utm_medium=email|_kx=)/i.test(location.search)) localStorage.setItem('cnc_from_email', '1');
      if (localStorage.getItem('cnc_from_email') === '1') alreadySubscribed = true;
    } catch (e) { /* storage blocked: decide on this URL alone */
      if (/[?&](utm_medium=email|_kx=)/i.test(location.search)) alreadySubscribed = true;
    }

    var stepPoll = root.querySelector('[data-cnc-popup-step="poll"]');
    var stepEmail = root.querySelector('[data-cnc-popup-step="email"]');
    var stepSuccess = root.querySelector('[data-cnc-popup-step="success"]');
    var form = root.querySelector('[data-cnc-popup-form]');
    var submitBtn = root.querySelector('[data-cnc-popup-submit]');
    var label = submitBtn && submitBtn.querySelector('[data-label]');
    var loading = submitBtn && submitBtn.querySelector('[data-loading]');
    var errorEl = root.querySelector('[data-cnc-popup-error]');
    var codeEl = root.querySelector('[data-cnc-popup-code]');
    var copyBtn = root.querySelector('[data-cnc-popup-copy]');
    var panel = root.querySelector('.cnc-popup__panel');

    var selectedFandom = null;   // poll answer — tags the profile + routes the welcome flow
    var openSource = null;       // which trigger fired the show (conversion-by-trigger)
    var opened = false;

    function popupId() {
      try {
        var v = localStorage.getItem(ID_KEY);
        if (!v) { v = 'p_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); localStorage.setItem(ID_KEY, v); }
        return v;
      } catch (e) { return null; }
    }
    function isSubmitted() { try { return localStorage.getItem(SUBMITTED_KEY) === '1'; } catch (e) { return false; } }
    function dismissedRecently() {
      try { var d = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10); return !!d && (Date.now() - d) < (suppressDays * 86400000); }
      catch (e) { return false; }
    }
    function tabRetired() {
      try {
        var d = parseInt(localStorage.getItem(TAB_OFF_KEY) || '0', 10);
        return !!d && (Date.now() - d) < (TAB_OFF_DAYS * 86400000);
      } catch (e) { return false; }
    }
    /* The wrapper is what carries [hidden] now — toggling the tab alone would
       leave the dismiss X floating on its own against the edge. */
    /* ONLY FOR PEOPLE WHO WOULD PAY SHIPPING (Tracy 2026-10-04: "it should only show if they have
       paid shipping"). The bag publishes data-cnc-ship-free="1" when this cart already ships free
       (sections/quick-cart.liquid, same rule as its own messages). The tab hides while that is true
       and comes back if the bag drops below the threshold: the bag re-renders its section on every
       change, which this observer sees. No cart / empty cart = no flag = they would pay = show. */
    var tabWanted = false, tabObserver = null;
    function shipFreeNow() { var el = document.querySelector('[data-cnc-ship-free]'); return !!el && el.getAttribute('data-cnc-ship-free') === '1'; }
    /* IN THE BAG TOO (Tracy 2026-10-04: "I'm asking for the free shipping bubble to be showing in the
       slide cart"). On a phone the bag is the whole screen and its bottom edge is the checkout block,
       so the circle rides just above that block, as it rides above the sticky add-to-bag bar on a
       product page. The block's height moves with its content (the shipping line comes and goes), so
       it is measured, not assumed. Where the bag is a side drawer clear of the circle's corner
       (desktop), the circle stays put. */
    function bagLift() {
      var foot = document.body.classList.contains('quick-cart-open') && document.querySelector('.quick-cart__footer');
      var r = foot && foot.getBoundingClientRect();
      if (r && r.height && r.left < 80) tabWrap.style.setProperty('bottom', Math.round(window.innerHeight - r.top + 12) + 'px', 'important');
      else tabWrap.style.removeProperty('bottom');
    }
    function applyTab() { if (tabWrap) { tabWrap.hidden = !tabWanted || shipFreeNow(); bagLift(); } }
    function reTab() { applyTab(); setTimeout(applyTab, 450); }   /* again once the bag's slide-in settles */
    function showTab(qa) {
      if (!qa && tabRetired()) return;   /* the QA preview shows it regardless */
      tabWanted = true; applyTab();
      if (!tabObserver && window.MutationObserver) {
        tabObserver = new MutationObserver(reTab);
        var qc = document.querySelector('[data-section-type="quick-cart"]');
        if (qc) tabObserver.observe(qc, { childList: true, subtree: true });
        tabObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        window.addEventListener('resize', applyTab, { passive: true });
      }
    }
    function hideTab() { tabWanted = false; if (tabWrap) tabWrap.hidden = true; }
    function retireTab() {
      try { localStorage.setItem(TAB_OFF_KEY, String(Date.now())); } catch (e) {}
      hideTab();
    }

    function logPopup(event, trigger) {
      if (window.CNC_INTERNAL) return;   /* our own visits are never counted (layout/theme.liquid) */
      try {
        fetch(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
          body: JSON.stringify({ event: event, popup_id: popupId(), trigger: trigger, country: country })
        });
      } catch (e) { /* never break the storefront */ }
    }

    function showStep(name) {
      if (stepPoll) stepPoll.hidden = name !== 'poll';
      if (stepEmail) stepEmail.hidden = name !== 'email';
      if (stepSuccess) stepSuccess.hidden = name !== 'success';
      if (name === 'success') revealSuccess();
    }
    function revealSuccess() {
      // Tracy 2026-09-25: the US moved onto the SAME model as everywhere else —
      // free shipping over $99, plus free shipping on the FIRST order via the
      // welcome code. So every market now gets the code block, and the
      // US-only "no code needed, it's automatic" block is retired.
      //
      // Retired, not deleted: if the always-free US rate ever comes back, flip
      // this one flag rather than re-deriving the branch. The flag is the whole
      // switch — `country` is still posted to the signup endpoint (lines ~97,
      // ~156), so nothing else depends on this comparison.
      var usAlwaysFree = false;
      var us = root.querySelector('[data-cnc-success="us"]');
      var row = root.querySelector('[data-cnc-success="row"]');
      if (us) us.hidden = !usAlwaysFree;
      if (row) row.hidden = usAlwaysFree;
    }

    function open(source, force) {
      if (opened) return;
      if (!force) {
        if (isSubmitted() || alreadySubscribed) return;      // already on the list
        if (dismissedRecently()) { showTab(); return; }       // they closed it — nudge only, don't nag
      }
      opened = true;
      openSource = source || 'unknown';
      hideTab();
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      showStep('poll');
      document.documentElement.style.overflow = 'hidden';
      if (panel) { try { panel.focus(); } catch (e) {} }
      logPopup('shown', openSource);
    }

    function close(reason) {
      if (!opened) return;
      opened = false;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (reason !== 'submitted') {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) {}
        showTab();   // leave the minimised nudge for later
      }
    }

    function submit(email) {
      submitBtn.disabled = true;
      if (label) label.hidden = true;
      if (loading) loading.hidden = false;
      if (errorEl) errorEl.hidden = true;

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          country: country,
          fandom: selectedFandom,
          source: 'cnc_popup',
          popup_id: popupId(),
          trigger: openSource,
          form_url: location.href,
          consent_text: 'Subscribed to Caffeine & Curses emails via the site pop-up'
        })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (d) {
          if (r.ok && d && d.subscribed) {
            try { localStorage.setItem(SUBMITTED_KEY, '1'); } catch (e) {}
            // Remember who this browser is, so a bag they leave behind can be reminded: the
            // first-party pixel sends it once with its next event and then removes it.
            try { localStorage.setItem('cnc_bind_email', email); } catch (e) {}
            hideTab();               // signed up — no more nudge
            showStep('success');
          } else if (errorEl) {
            errorEl.textContent = "Couldn't sign you up. Try again in a moment."; errorEl.hidden = false;
          }
        });
      }).catch(function () {
        if (errorEl) { errorEl.textContent = "Couldn't sign you up. Try again in a moment."; errorEl.hidden = false; }
      }).then(function () {
        submitBtn.disabled = false;
        if (label) label.hidden = false;
        if (loading) loading.hidden = true;
      });
    }

    /* --- Wire interactions --- */
    root.addEventListener('click', function (e) {
      var optBtn = e.target.closest('[data-cnc-popup-poll]');
      if (optBtn) { selectedFandom = optBtn.getAttribute('data-cnc-popup-poll'); showStep('email'); return; }
      if (e.target.closest('[data-cnc-popup-poll-skip]')) { close('poll_decline'); return; }  // "No thanks" DISMISSES (like the X) — never forces the email step
      if (e.target.closest('[data-cnc-popup-back]')) { showStep('poll'); return; }
      if (e.target.closest('[data-cnc-popup-close]')) { close('user_close'); return; }
      if (e.target.closest('[data-cnc-popup-copy]')) {
        var code = codeEl && codeEl.textContent.trim();
        if (code && navigator.clipboard) {
          navigator.clipboard.writeText(code).then(function () {
            if (copyBtn) {
              var orig = copyBtn.innerHTML;
              copyBtn.innerHTML = '<span style="font-size:1.2rem;font-weight:600;line-height:1">Copied ✓</span>';
              setTimeout(function () { copyBtn.innerHTML = orig; }, 1500);
            }
          });
        }
        return;
      }
    });

    if (tab) tab.addEventListener('click', function () { open('minimised_tab', true); });
    if (tabDismiss) {
      tabDismiss.addEventListener('click', function (e) {
        /* stopPropagation as well as the sibling markup: the strip and the X sit
           inside one wrapper, and without this a tap that lands on the boundary
           would retire the tab AND open the popup. */
        e.preventDefault();
        e.stopPropagation();
        retireTab();
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = (form.querySelector('input[name="email"]').value || '').trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (errorEl) { errorEl.textContent = 'Please enter a valid email.'; errorEl.hidden = false; }
          return;
        }
        submit(email);
      });
    }

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && opened) close('escape'); });

    /* --- What to show on load --- */
    if (/[?#&]cncpopup\b/i.test(location.href)) {          // QA force-open
      open('qa', true);
      return;
    }
    /* QA preview of a test arm, for Tracy (2026-10-04: "No tab showed for me at all in
       staging"). ?cncab=tab shows the side tab, ?cncab=auto opens the popup, regardless of
       this browser's own state (already signed up, logged-in subscriber, tab dismissed).
       Nothing is logged and the cart is not tagged, so a QA visit is never counted. */
    var qaArm = (location.href.match(/[?&#]cncab=(tab|auto)\b/i) || [])[1];
    if (qaArm) {
      if (qaArm.toLowerCase() === 'tab') { showTab(true); }   /* same rules as a shopper sees: bag lift, paid-shipping only */
      else { open('qa', true); }
      return;
    }
    /* 50/50 TEST: auto-pop vs tab-only (Tracy 2026-10-04: "doing 50-50 splits a great idea").
       Each browser is assigned once, at its first eligible visit, to 'auto' (today's automatic
       popup) or 'tab' (side tab only; the popup opens only when tapped), and keeps that arm.
       Measurement, no schema change:
         - exposure: one 'shown' event with trigger 'ab_assign_auto' / 'ab_assign_tab' and the
           browser's popup_id (email_signup_popup_event) at assignment;
         - sign-ups: the existing events carry the same popup_id, so they join back to the arm;
         - orders: hidden cart attributes _cnc_popup_ab + _cnc_popup_id, written once per
           browser session, land on the order's note_attributes.
       No storage (private mode etc.) -> no arm: the visitor gets 'auto' and is not counted,
       so nobody flips between arms. To end the test, set AB_FORCE to 'tab' or 'auto'. */
    var AB_KEY = 'cnc_popup_ab_v1', AB_CART_KEY = 'cnc_popup_ab_cart_v1', AB_FORCE = null;
    function abRead() {
      if (AB_FORCE) return AB_FORCE;
      try { var a = localStorage.getItem(AB_KEY); return (a === 'tab' || a === 'auto') ? a : null; } catch (e) { return null; }
    }
    function abAssign() {
      if (window.CNC_INTERNAL) return null;   /* our own browsers never join an arm, so switching test mode off later can't credit an arm it never counted */
      if (AB_FORCE) return AB_FORCE;
      try {
        var a = Math.random() < 0.5 ? 'tab' : 'auto';
        localStorage.setItem(AB_KEY, a);
        if (localStorage.getItem(AB_KEY) !== a) return null;
        logPopup('shown', 'ab_assign_' + a);
        return a;
      } catch (e) { return null; }
    }
    function abTagCart(a) {
      if (window.CNC_INTERNAL) return;   /* ditto: our carts never join a test arm */
      try {
        if (sessionStorage.getItem(AB_CART_KEY) === a) return;
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _cnc_popup_ab: a, _cnc_popup_id: popupId() } })
        }).then(function (r) { if (r.ok) { try { sessionStorage.setItem(AB_CART_KEY, a); } catch (e) {} } }).catch(function () {});
      } catch (e) { /* never break the storefront */ }
    }
    var abArm = abRead();
    if (abArm) abTagCart(abArm);

    if (isSubmitted() || alreadySubscribed) return;         // done — no popup, no tab
    if (dismissedRecently()) { showTab(); return; }         // closed before — just the corner nudge
    if (tabRetired()) return;                               // they X'd the strip — leave them alone

    /* 'tab' arm: no auto-pop (Tracy 2026-10-04: "we would still have the pop-up available.
       We're just not going to auto-pop it"). The side tab shows straight away and the popup
       opens only when tapped. Baseline, 30 days to 3 Oct: the automatic triggers opened it
       ~15,000 times for ~490 sign-ups (97% closed it); the tab, then shown only AFTER a
       dismissal, was tapped 65 times for 19 sign-ups (29%). 'auto' arm falls through to the
       original triggers below, unchanged. */
    if (!abArm) { abArm = abAssign(); if (abArm) abTagCart(abArm); }
    if (abArm === 'tab') { showTab(); return; }

    /* fresh visitor — arm the triggers to auto-open the full popup */
    setTimeout(function () { open('time'); }, triggerSeconds * 1000);

    var scrollFired = false, lastY = window.scrollY, lastT = Date.now();
    var armedAt = Date.now();
    /* Fast scroll-up is our LEAVE signal, and on mobile it is the ONLY one — the
       exit-intent handler below is desktop-only (mouseout needs a cursor).

       It needs a short grace period, site-wide, for two reasons. A quick flick
       upward is also how someone re-checks a photo or a price before committing,
       and measured median time to press Add to Bag is 22s — so an instantly-armed
       leave signal lands in the middle of the decision. It is also spoofable by the
       page itself: lazy-loaded media shifting layout produces a large negative
       scroll delta that looks identical to a deliberate flick. 20s is just under
       that 22s median. */
    var SCROLL_UP_GRACE_MS = 20000;
    function onScroll() {
      if (opened) return;
      var y = window.scrollY, t = Date.now(), dy = y - lastY, dt = (t - lastT) || 1;
      if (dy < -100 && (-dy / dt) > 1.0 && (t - armedAt) >= SCROLL_UP_GRACE_MS) {
        lastY = y; lastT = t; open('scroll_up'); return;
      }
      lastY = y; lastT = t;
      if (scrollFired) return;
      var sh = document.documentElement.scrollHeight - window.innerHeight;
      if (sh <= 0) return;
      if ((y / sh) * 100 >= triggerScroll) { scrollFired = true; open('scroll'); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    if (window.matchMedia && window.matchMedia('(min-width: 990px)').matches) {
      document.addEventListener('mouseout', function (e) {
        if (opened) return;
        if (e.clientY <= 0 && !e.relatedTarget) open('exit_intent');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
